import React, {cloneElement, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import {useQuery} from "@apollo/react-hooks";
import {GET_TEAM_MEMBERSHIPS} from "../../database/queries/teamQueries";
import {GET_INTEGRATIONS} from "../../database/queries/integrationQueries";
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import SupervisedUserCircleIcon from '@material-ui/icons/SupervisedUserCircle';
import {SizeMe} from "react-sizeme";
import useLayoutStore from "../../layoutStore";
import Grid from "@material-ui/core/Grid";
import ConfirmationDialog from "../../dialogs/confirmation";
import usePersistentStore from "../../persistentStore";
import {Link, Route, Switch, useRouteMatch} from "react-router-dom";

const inIFrame = (window.parent.window !== window);

const useStyles = makeStyles(theme => ({
    tabs: {
        borderRight: `1px solid ${theme.palette.divider}`,
        minHeight: 24,
        '& .MuiTab-root': {
            minHeight: 24,
        }
    },
    stifle: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        width: '100%',
        textAlign: 'left',
        fontSize: '90%'
    },
    tabPanel: {
        width: '100%'
    },
    noMargin: {
        marginBottom: 0,
        minHeight: 0
    },
    gridNoPadding: {
        padding: 0,
        margin: 0
    },
    leftColumn: {
        padding: 0,
        margin: 0,
    },
    boxNoPadding: {
        padding: 0,
        margin: 0
    },
    selectedTab: {
        border: '1px solid #108FC0',
    },
    tab: {}
}));

function TabPanel(props) {
    const classes = useStyles();
    const {children, value, index, ...other} = props;

    return (
        <Typography
            component="div"
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            className={classes.tabPanel}
            {...other}
        >
            {value === index && <Box p={1} m={0}>{children}</Box>}
        </Typography>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired,
};

function a11yProps(index) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

const INITIAL_SHARES = [
    {
        shareCategoryType: 'personal',
        shareCategoryId: 'personal',
        shareCategoryName: 'Personal',
        icon: <PersonOutlineIcon style={{verticalAlign: 'middle'}}/>
    },
];

const INITIAL_DIALOG = {
    title: "Stop Editing",
    question: 'Are you sure you want to stop editing?',
    yesHandler: null,
    noHandler: null,
};

const ShareTabs = (props) => {
    const classes = useStyles();
    const [currentTab, setCurrentTab] = useState(0);
    const [shares, setShares] = useState([...INITIAL_SHARES]);
    const [globalPersistentState, globalPersistentActions] = usePersistentStore();
    const [globalLayoutState] = useLayoutStore();
    const [teams, setTeams] = useState([]);
    const [integrations, setIntegrations] = useState([]);
    const [confirmation, setConfirmation] = useState(INITIAL_DIALOG);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    let {url} = useRouteMatch();

    const {data: dataTeams} = useQuery(GET_TEAM_MEMBERSHIPS, {
        variables: {
            orderList: ['teamName ASC']
        },
    });

    const {data: dataIntegrations} = useQuery(GET_INTEGRATIONS, {
        variables: {
            orderList: ['integrationName ASC']
        },
    });

    const sortCompare = (a, b) => {
        if (a.shareCategoryId === 'personal') {
            return -1
        } else if (b.shareCategoryId === 'personal') {
            return 1
        } else if (a.shareCategoryName < b.shareCategoryName) {
            return -1;
        } else if (a.shareCategoryName > b.shareCategoryName) {
            return 1;
        } else {
            return 0;
        }
    };

    useEffect(() => {
        if (dataTeams && dataTeams.teamMemberships) {
            let fetchedTeams = [];
            for (let t = 0; t < dataTeams.teamMemberships.length; t++) {
                const team = dataTeams.teamMemberships[t];
                fetchedTeams.push({
                    shareCategoryType: 'team',
                    shareCategoryId: team.teamId,
                    shareCategoryName: team.teamName,
                    icon: <PeopleOutlineIcon style={{verticalAlign: 'middle'}}/>
                });
            }
            setTeams(fetchedTeams);
        }
    }, [dataTeams]);

    useEffect(() => {
        if (dataIntegrations && dataIntegrations.integrations) {
            let fetchedIntegrations = [];
            for (let i = 0; i < dataIntegrations.integrations.length; i++) {
                const integration = dataIntegrations.integrations[i];
                fetchedIntegrations.push({
                    shareCategoryType: 'integration',
                    shareCategoryId: integration.integrationId,
                    shareCategoryName: integration.integrationName,
                    icon: <SupervisedUserCircleIcon style={{verticalAlign: 'middle'}}/>
                });
            }
            setIntegrations(fetchedIntegrations);
        }
    }, [dataIntegrations]);

    useEffect(() => {
        let merged = [...INITIAL_SHARES].concat(teams, integrations).sort(sortCompare);
        setShares(merged);
    }, [teams, integrations]);

    const shouldChangeTabs = (objName, callback) => {
        let leftOver = null;
        if (globalPersistentState[objName]) leftOver = objName;

        if (globalPersistentState.dirty && leftOver) {
            setConfirmation({
                ...INITIAL_DIALOG,
                question: 'Are you sure you want to stop editing the ' + leftOver + '?',
                yesHandler: () => {
                    globalPersistentActions.clearBiggie(objName);
                    if (globalPersistentState.dirty) globalPersistentActions.setDirty(false);
                    setConfirmationOpen(false);
                    callback(true);
                },
                noHandler: () => {
                    setConfirmationOpen(false);
                    callback(false);
                },
            });
            setConfirmationOpen(true);
        } else {
            if (objName) globalPersistentActions.clearBiggie(objName);
            if (globalPersistentState.dirty) globalPersistentActions.setDirty(false);
            callback(true);
        }
    };

    const handleChange = (event, newTab) => {
        shouldChangeTabs(props.shareParent, (yesIShould) => {
            if (yesIShould) setCurrentTab(newTab);
        });
    };

    return (
        <div>
            {shares && (
                <div className={classes.shares}>
                    <SizeMe>
                        {({size}) => {
                            let height = size.height;
                            if (!height) height = window.innerHeight;
                            let otherStuffHeight = 220;
                            if (inIFrame) otherStuffHeight = 30;
                            const newTabHeight = height - (globalLayoutState.headerHeight + globalLayoutState.tabBarHeight + otherStuffHeight);

                            return (
                                <div>
                                    <Grid container className={classes.gridNoPadding}>
                                        <Grid
                                            item xs={3}
                                            style={shares.length > 1 ? {} : {display: 'none'}}
                                            className={classes.leftColumn}>
                                            <Box p={1}>
                                                <Tabs
                                                    id="vertical-menu"
                                                    name="vertical-menu"
                                                    orientation="vertical"
                                                    value={currentTab}
                                                    variant="scrollable"
                                                    onChange={handleChange}
                                                    aria-label="Vertical Menu"
                                                    p={1} m={0}
                                                    className={classes.tabs}
                                                    style={{height: newTabHeight}}
                                                >
                                                    {shares.map((share, idx) => {
                                                        return (
                                                            <Tab
                                                                key={idx}
                                                                className={shares[currentTab].shareCategoryId === share.shareCategoryId ? classes.selectedTab : classes.tab}
                                                                name={share.shareCategoryName.replace(/\s+/g, '-').toLowerCase()}
                                                                aria-label={share.shareCategoryName.replace(/\s+/g, '-').toLowerCase()}
                                                                component={Link}
                                                                to={`${url}/${shares[idx].shareCategoryType}/${shares[idx].shareCategoryId}`}
                                                                label={
                                                                    <Typography
                                                                        className={classes.stifle}>
                                                                        {share.icon} {share.shareCategoryName}
                                                                    </Typography>
                                                                }
                                                                {...a11yProps(idx)}
                                                            />
                                                        );
                                                    })}
                                                </Tabs>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={shares.length > 1 ? 9 : 12} className={classes.gridNoPadding}>
                                            <Switch>
                                                {shares.map((share, idx) => {
                                                        return (
                                                            <Route key={idx}
                                                                   path={`${url}/${shares[idx].shareCategoryType}/${shares[idx].shareCategoryId}`}>
                                                                <TabPanel id={`tp-$idx`} key={idx} value={currentTab}
                                                                          index={idx}>
                                                                    {cloneElement(props.parent.component, {
                                                                        ...props,
                                                                        shareCategoryType: shares[idx].shareCategoryType,
                                                                        shareCategoryId: shares[idx].shareCategoryId,
                                                                        shareCategoryName: shares[idx].shareCategoryName
                                                                    })}
                                                                </TabPanel>
                                                            </Route>
                                                        )
                                                    }
                                                )}
                                            </Switch>
                                        </Grid>
                                    </Grid>
                                </div>
                            )
                        }}
                    </SizeMe>
                </div>
            )}
            <ConfirmationDialog
                open={confirmationOpen}
                title={confirmation.title}
                question={confirmation.question}
                handleYes={confirmation.yesHandler}
                handleNo={confirmation.noHandler}
            />
        </div>
    );
};

export default ShareTabs;
